/**
 * WHAT: Application Configuration Module
 * WHY: Centralize configuration settings:
 *      1. API endpoints and URLs
 *      2. Environment-specific settings
 *      3. Feature flags and constants
 * HOW: Using environment detection and configuration objects
 * IMPORTANCE: Maintainable and environment-aware configuration
 */

/**
 * WHAT: Environment Detection
 * WHY: Determine current runtime environment
 * HOW: Check hostname and environment variables
 * IMPORTANCE: Configure services appropriately
 */
const getEnvironment = () => {
    const hostname = window.location.hostname;
    let env = 'production'; // Default to production
    
    // Check for development environments
    if (hostname === 'localhost' || 
        hostname.includes('127.0.0.1') || 
        hostname.includes('192.168.')) {
        env = 'development';
    }
    // Check for test environment
    else if (hostname.includes('test.')) {
        env = 'test';
    }
    
    return env;
};

// Initialize environment once
const CURRENT_ENV = getEnvironment();

// Helper functions declaration first
const getApiUrl = (baseUrl, apiPath, endpoint) => {
    if (!baseUrl) {
        console.error('API base URL is not configured');
        return '';
    }
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${baseUrl}${apiPath}${cleanEndpoint}`.replace(/([^:]\/)\/+/g, '$1');
};

const getAssetUrl = (baseUrl, path) => {
    if (!baseUrl) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`.replace(/([^:]\/)\/+/g, '$1');
};

// Environment-specific configurationss
const resolveDevelopmentApiBaseUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:3002';
    const host = window.location.hostname;
    const port = window.location.port;
    if (host === 'localhost' || host === '127.0.0.1') {
        if (port === '3002' || port === '3000') {
            return window.location.origin;
        }
    }
    return 'http://localhost:3002';
};

const ENV_CONFIG = {
    development: {
        api: {
            get baseUrl() {
                return resolveDevelopmentApiBaseUrl();
            },
            apiPath: '/api'
        }
    },
    test: {
        api: {
            baseUrl: 'https://test.sourdoughies.com',
            apiPath: '/api'
        }
    },
    production: {
        api: {
            baseUrl: 'https://sourdoughies.com',
            apiPath: '/api'
        }
    }
};

// Base configuration
const BASE_CONFIG = {
    api: {
        baseUrl: ENV_CONFIG[CURRENT_ENV]?.api?.baseUrl || '',
        apiPath: ENV_CONFIG[CURRENT_ENV]?.api?.apiPath || '/api',
        timeout: 30000, // 30 seconds
    },
    auth: {
        tokenKey: 'token',
        userKey: 'user',
        cookieName: 'auth_token',
        tokenRefreshInterval: 5 * 60 * 1000, // 5 minutes
        sessionTimeout: 24 * 60 * 60 * 1000 // 24 hours
    },
    upload: {
        maxSize: 5 * 1024 * 1024, // 5MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        path: '/uploads'
    },
    security: {
        passwordMinLength: 8,
        maxLoginAttempts: 5,
        lockoutTime: 15 * 60 * 1000, // 15 minutes
    },
    ui: {
        toastDuration: 3000, // 3 seconds
        maxImageSize: 5 * 1024 * 1024 // 5MB
    }
};

// Create CONFIG object
const CONFIG = {
    ...BASE_CONFIG,
    ...ENV_CONFIG[CURRENT_ENV],
    environment: CURRENT_ENV,
    features: {
        enableCache: CURRENT_ENV !== 'development',
        debugLogging: CURRENT_ENV === 'development',
        mockData: false,
        maintenanceMode: false
    },
    endpoints: {
        auth: '/auth',
        login: '/auth/login',
        verify: '/auth/verify',
        refresh: '/auth/refresh',
        menu: '/menu',
        menuItem: (id) => `/menu/items/${id}`,
        menuCategories: '/menu/categories',
        menuCategory: (name) => `/menu/categories/${name}`
    },
    files: {
        allowedTypes: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        maxFiles: 5,
        maxSize: 5 * 1024 * 1024, // 5MB
        uploadDir: '/uploads',
        urlFormat: `${BASE_CONFIG.api.baseUrl}/uploads/[filename]`
    },
    app: {
        maxUploadSize: 5 * 1024 * 1024, // 5MB
        supportedImageTypes: ['image/jpeg', 'image/png', 'image/webp'],
        menuRefreshInterval: 5 * 60 * 1000, // 5 minutes
        images: {
            noImage: {
                path: '/images/icons/no-image2.svg',  // Primary fallback - customizable
                // Secondary fallback - guaranteed to work even if server files are missing
                fallback: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlZWUiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzk5OSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+'
            }
        }
    },
    isDevelopment: CURRENT_ENV === 'development',
    getBasePath: () => window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/')) || '/',
};

// Add wrapped helper functions to CONFIG
CONFIG.getApiUrl = (endpoint) => getApiUrl(CONFIG.api.baseUrl, CONFIG.api.apiPath, endpoint);
CONFIG.getAssetUrl = (path) => getAssetUrl(CONFIG.api.baseUrl, path);

// Freeze configuration to prevent modifications
Object.freeze(CONFIG);
Object.freeze(CONFIG.api);
Object.freeze(CONFIG.auth);
Object.freeze(CONFIG.upload);
Object.freeze(CONFIG.security);
Object.freeze(CONFIG.features);
Object.freeze(CONFIG.endpoints);
Object.freeze(CONFIG.files);
Object.freeze(CONFIG.app);

// Make configuration available globally
window.CONFIG = CONFIG;

// Export configuration and helper functions
export { CONFIG, getApiUrl, getAssetUrl };
export default CONFIG;
